import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AdvancedSearchState } from './advanced-search.reducer';

export const selectAdvancedSearchState = createFeatureSelector<AdvancedSearchState>('advancedSearch');

// Company search selectors
export const selectNextPageToken = createSelector(
  selectAdvancedSearchState,
  (state) => state.nextPageToken
);

export const selectCompaniesResponse = createSelector(
  selectAdvancedSearchState,
  (state) => state.companiesResponse
);

export const selectCompanies = createSelector(
  selectAdvancedSearchState,
  (state) => state.companies
);

export const selectLoading = createSelector(
  selectAdvancedSearchState,
  (state) => state.loading
);

export const selectError = createSelector(
  selectAdvancedSearchState,
  (state) => state.error
);

export const selectTotalRecords = createSelector(
  selectAdvancedSearchState,
  (state) => state.totalRecords
);

export const selectPaginationCount = createSelector(
  selectAdvancedSearchState,
  (state) => state.totalRecords || 0
);

export const selectCurrentPage = createSelector(
  selectAdvancedSearchState,
  (state) => state.currentPage
);

export const selectPageSize = createSelector(
  selectAdvancedSearchState,
  (state) => state.pageSize
);

export const selectAdvancedSearchSuccess = createSelector(
  selectAdvancedSearchState,
  (state) => state.companies && state.companies.length > 0 ? state.companies : null
);

export const selectAdvancedSearchFailure = createSelector(
  selectAdvancedSearchState,
  (state) => state.error
);

export const selectPreviousPageToken = createSelector(
  selectAdvancedSearchState,
  (state) => state?.previousPageToken || null
);

// People selectors
export const selectPeopleResults = createSelector(
  selectAdvancedSearchState,
  (state) => state.peopleResults
);

export const selectPeople = createSelector(
  selectPeopleResults,
  (results) => results?.people || []
);

export const selectPeopleTotal = createSelector(
  selectPeopleResults,
  (results) => results?.pagination?.total_entries || 0
);

export const selectPeoplePagination = createSelector(
  selectPeopleResults,
  (results) => results?.pagination || null
);

export const selectSelectedCompany = createSelector(
  selectAdvancedSearchState,
  (state) => state.selectedCompany
);

// Filter selectors
export const selectSearchFilters = createSelector(
  selectAdvancedSearchState,
  (state) => state.searchFilters
);

// The exact built API payload ({ and: [...] }) of the current search, reused
// verbatim when paginating so page N's query matches page 1's query.
export const selectSearchPayload = createSelector(
  selectAdvancedSearchState,
  (state) => state.searchPayload
);

export const selectSavedFilters = createSelector(
  selectAdvancedSearchState,
  (state) => state.savedFilters || state.searchFilters
);

export const selectFilterTypes = createSelector(
  selectAdvancedSearchState,
  (state) => state.filterTypes
);

export const selectHasSavedFilters = createSelector(
  selectSavedFilters,
  (filters) => !!filters
);

// NEW Location selectors - Store-based approach
export const selectCountriesList = createSelector(
  selectAdvancedSearchState,
  (state) => state.countriesList || []
);

export const selectRegionsByCountry = createSelector(
  selectAdvancedSearchState,
  (state) => state.regionsByCountry || {}
);

export const selectCitiesByRegion = createSelector(
  selectAdvancedSearchState,
  (state) => state.citiesByRegion || {}
);

export const selectRegionsForCountry = (countryId: string | number) => createSelector(
  selectRegionsByCountry,
  (regionsByCountry) => regionsByCountry[countryId] || []
);

export const selectCitiesForRegion = (regionId: string | number) => createSelector(
  selectCitiesByRegion,
  (citiesByRegion) => citiesByRegion[regionId] || []
);

export const selectCountriesLoading = createSelector(
  selectAdvancedSearchState,
  (state) => state.loadingCountries
);

export const selectRegionLoading = (countryId: string | number) => createSelector(
  selectAdvancedSearchState,
  (state) => state.loadingRegions[countryId] || false
);

export const selectCityLoading = (regionId: string | number) => createSelector(
  selectAdvancedSearchState,
  (state) => state.loadingCities[regionId] || false
);

// Company Industries selectors
export const selectCompanyIndustries = createSelector(
  selectAdvancedSearchState,
  (state: AdvancedSearchState) => state.companyIndustries
);

export const selectLoadingCompanyIndustries = createSelector(
  selectAdvancedSearchState,
  (state: AdvancedSearchState) => state.loadingCompanyIndustries
);

export const selectCompanyIndustriesError = createSelector(
  selectAdvancedSearchState,
  (state: AdvancedSearchState) => state.companyIndustriesError
);

// Legacy selectors (for backward compatibility)
export const selectLocations = createSelector(
  selectAdvancedSearchState,
  (state) => state.locations?.data || []
);

export const selectLoadingLocations = createSelector(
  selectAdvancedSearchState,
  (state) => state.loadingLocations
);

export const selectCountries = createSelector(
  selectAdvancedSearchState,
  (state) => {
    if (!state) {
      return [];
    }

    let countriesArray = [];

    try {
      if (state.countries?.data?.countries) {
        countriesArray = state.countries.data.countries;
      } else if (state.countries?.data?.countries) {
        countriesArray = state.countries.data.countries;
      } else if (Array.isArray(state.countries?.data)) {
        countriesArray = state.countries.data;
      }
    } catch (error) {
      console.error('Error extracting countries:', error);
    }

    if (Array.isArray(countriesArray) && countriesArray.length > 0) {
      return countriesArray.map((country, index) => ({
        id: index + 1,
        name: country.name,
        value: country.id
      }));
    }

    return [];
  }
);

export const selectRegions = createSelector(
  selectAdvancedSearchState,
  (state) => {
    let regionsArray = [];

    try {
      if (state.regions?.data?.data?.regions) {
        regionsArray = state.regions.data.data.regions;
      } else if (state.regions?.data?.regions) {
        regionsArray = state.regions.data.regions;
      } else if (Array.isArray(state.regions?.data)) {
        regionsArray = state.regions.data;
      }
    } catch (error) {
      console.error('Error extracting regions:', error);
    }

    if (Array.isArray(regionsArray) && regionsArray.length > 0) {
      return regionsArray.map((region, index) => ({
        id: index + 1,
        name: region.name,
        value: region.id || region.veridion_region_id
      }));
    }

    return [];
  }
);

export const selectCities = createSelector(
  selectAdvancedSearchState,
  (state) => {
    let citiesArray = [];

    try {
      if (state.cities?.data?.data?.cities) {
        citiesArray = state.cities.data.data.cities;
      } else if (state.cities?.data?.cities) {
        citiesArray = state.cities.data.cities;
      } else if (Array.isArray(state.cities?.data)) {
        citiesArray = state.cities.data;
      }
    } catch (error) {
      console.error('Error extracting cities:', error);
    }

    if (Array.isArray(citiesArray) && citiesArray.length > 0) {
      return citiesArray.map((city, index) => ({
        id: index + 1,
        name: city.name,
        value: city.id || city.veridion_city_id
      }));
    }

    return [];
  }
);

export const selectLoadingCountries = createSelector(
  selectAdvancedSearchState,
  (state) => state.loadingCountries
);

export const selectLoadingRegions = createSelector(
  selectAdvancedSearchState,
  (state) => state.loadingRegions
);

export const selectLoadingCities = createSelector(
  selectAdvancedSearchState,
  (state) => state.loadingCities
);

export const selectIsNewSearch = createSelector(
  selectAdvancedSearchState,
  (state) => state.isNewSearch || false
);
